import React, {useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {format} from 'date-fns';
import {fr} from 'date-fns/locale';

// @mui
import {styled} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Fade from '@mui/material/Fade';
import Grow from '@mui/material/Grow';
import Skeleton from '@mui/material/Skeleton';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

// icons
import RefreshIcon from '@mui/icons-material/Refresh';

// components
import {TimeSlotPicker} from '../components/TimeSlotPicker';

// API
import {useExternalTimeSlots} from 'packages/common/hooks/api/widgets/table-reservations/Reservations';

// constants
import {ERROR_MESSAGES} from '../constants/reservation';

// ----------------------------------------------------------------------

const RefreshButton = styled(IconButton)(({theme}) => ({
  color: theme.palette.primary.main,
  padding: theme.spacing(0.5),
}));

// Skeleton loader pour les horaires
const TimeSlotSkeletonLoader = styled(Box)(({theme}) => ({
  width: '100%',
  padding: theme.spacing(1),
}));

// Composant stylisé pour le conteneur de timeslots avec animation
const TimeSlotContainer = styled(Box)(({theme}) => ({
  opacity: 1,
  transition: 'opacity 0.4s ease-in-out',
}));

// ----------------------------------------------------------------------

export function TimeStep({
  formData,
  updateFormData,
  config,
  companyId,
  apiKey,
  setValidation,
  setErrors,
  errors,
  setStepReady, // Add this new prop
}) {
  const [timeSlots, setTimeSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTimeSlotsVisible, setIsTimeSlotsVisible] = useState(false);
  const [fetchAttempts, setFetchAttempts] = useState({});

  // Référence pour éviter les requêtes multiples
  const fetchingRef = useRef(false);
  const dateRef = useRef(null);

  // Api hooks
  const {getTimeSlotsForDate, loading: loadingTimeSlots} = useExternalTimeSlots();

  // Fonction de validation
  const validateStep = React.useCallback(() => {
    if (!formData.timeSlot) {
      setErrors({timeSlot: ERROR_MESSAGES.TIME_REQUIRED});
      return false;
    }
    return true;
  }, [formData.timeSlot, setErrors]);

  // Enregistrer la fonction de validation auprès du parent
  useEffect(() => {
    setValidation(validateStep);
  }, [setValidation, formData.timeSlot, validateStep]);

  // Effect to control next button state based on time slot selection
  useEffect(() => {
    if (setStepReady) {
      // Enable button only when a time slot is selected
      setStepReady(!!formData.timeSlot);
    }
  }, [formData.timeSlot, setStepReady]);

  // Fetch time slots when date changes
  useEffect(() => {
    // Vérifier si les données nécessaires sont disponibles
    if (!companyId || !formData.date || !formData.service || !formData.partySize) {
      return;
    }

    // Vérifier si nous avons déjà chargé les données pour cette date
    const dateString = format(formData.date, 'yyyy-MM-dd');

    // Limiter à 2 tentatives maximum par date
    if (fetchAttempts[dateString] && fetchAttempts[dateString] >= 2) {
      setErrors({api: 'Impossible de charger les créneaux. Veuillez réessayer plus tard.'});
      return;
    }

    // Si nous avons déjà des données pour cette date, ne pas refaire la requête
    if (dateRef.current === dateString) {
      setIsTimeSlotsVisible(true);
      return;
    }

    // Si nous sommes déjà en train de charger des données, ne pas lancer une nouvelle requête
    if (fetchingRef.current) {
      return;
    }

    // Marquer que nous sommes en train de charger des données
    fetchingRef.current = true;
    setIsLoading(true);
    setIsTimeSlotsVisible(false); // Masquer les slots pendant le chargement

    // Effacer les erreurs
    if (errors.timeSlot || errors.api) {
      setErrors({});
    }

    // Mettre à jour la référence de date
    dateRef.current = dateString;

    // Incrémenter le compteur de tentatives
    setFetchAttempts((prev) => ({
      ...prev,
      [dateString]: (prev[dateString] || 0) + 1,
    }));

    // Fonction pour charger les données
    const fetchTimeSlots = async () => {
      try {
        const params = {
          date: dateString,
          service: formData.service,
          partySize: formData.partySize,
        };

        const response = await getTimeSlotsForDate(companyId, params, apiKey);

        // Traitement normal des créneaux
        let extractedTimeSlots = [];
        if (response) {
          if (response.timeslots && Array.isArray(response.timeslots)) {
            extractedTimeSlots = response.timeslots;
          } else if (Array.isArray(response)) {
            extractedTimeSlots = response;
          }
          setTimeSlots(extractedTimeSlots);
        } else {
          setTimeSlots([]);
        }

        // Petit délai pour l'animation d'apparition des créneaux horaires
        setTimeout(() => {
          setIsTimeSlotsVisible(true);
        }, 100);
      } catch (apiError) {
        setErrors({
          api: apiError.response?.data?.message || ERROR_MESSAGES.GENERAL_ERROR,
        });
        setTimeSlots([]);
        setIsTimeSlotsVisible(true); // Afficher même en cas d'erreur
      } finally {
        setIsLoading(false);
        // Réinitialiser le drapeau
        fetchingRef.current = false;
      }
    };

    fetchTimeSlots();
  }, [companyId, apiKey, formData.date, formData.service, formData.partySize, fetchAttempts, getTimeSlotsForDate, errors.api, errors.timeSlot, setErrors]);

  // Fonction pour forcer le rafraîchissement des données
  const handleRefresh = () => {
    // Effacer la référence de date pour forcer un rechargement
    dateRef.current = null;
    fetchingRef.current = false;
    setErrors({});
    setTimeSlots([]);
    setIsTimeSlotsVisible(false); // Réinitialiser l'animation
  };

  // Handle time slot selection
  const handleTimeSelect = (time) => {
    updateFormData('timeSlot', time);

    // Effacer les erreurs si elles existent
    if (errors.timeSlot) {
      setErrors({});
    }

    // No need to call setStepReady here as the effect will handle it
  };

  // Skeleton loader pour les horaires
  const renderTimeSlotSkeleton = () => (
    <TimeSlotSkeletonLoader>
      <Grid container spacing={1.5}>
        {/* 3 lignes de 4 créneaux pour le skeleton */}
        {[...Array(3)].map((_, rowIndex) => (
          <Grid container item spacing={1} key={`row-${rowIndex}`}>
            {[...Array(4)].map((_, colIndex) => (
              <Grid item xs={3} key={`slot-${rowIndex}-${colIndex}`}>
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={36}
                  animation="wave"
                />
              </Grid>
            ))}
          </Grid>
        ))}
      </Grid>
    </TimeSlotSkeletonLoader>
  );

  return (
    <Fade in timeout={300}>
      <Box>
        <Typography variant="h6" sx={{mb: 2, textAlign: 'center'}}>
          Quel horaire souhaitez-vous réserver ?
        </Typography>

        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
        }}>
          <Typography variant="body2" sx={{fontSize: '0.85rem'}}>
            Horaires disponibles le {format(formData.date, 'd MMMM', {locale: fr})}
          </Typography>
          <RefreshButton
            onClick={handleRefresh}
            size="small"
            disabled={isLoading || loadingTimeSlots}
          >
            <RefreshIcon fontSize="small" />
          </RefreshButton>
        </Box>

        {isLoading || loadingTimeSlots ? (
          <Fade in timeout={300}>
            {renderTimeSlotSkeleton()}
          </Fade>
        ) : timeSlots.length === 0 ? (
          <Grow in timeout={300}>
            <Alert severity="info" sx={{mb: 2, fontSize: '0.8rem', py: 0.5}}>
              {errors.api || ERROR_MESSAGES.NO_AVAILABILITY}
            </Alert>
          </Grow>
        ) : (
          <Grow in={isTimeSlotsVisible} timeout={400}>
            <TimeSlotContainer>
              <TimeSlotPicker
                timeSlots={timeSlots}
                selectedTime={formData.timeSlot}
                onTimeSelect={handleTimeSelect}
                compact={true}
                showGroupHeaders={false}
              />
            </TimeSlotContainer>
          </Grow>
        )}

        {errors.timeSlot && timeSlots.length > 0 && (
          <Alert severity="error" sx={{mt: 2, mb: 2, fontSize: '0.8rem', py: 0.5}}>
            {errors.timeSlot}
          </Alert>
        )}
      </Box>
    </Fade>
  );
}

TimeStep.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
  config: PropTypes.object,
  companyId: PropTypes.string,
  apiKey: PropTypes.string,
  setValidation: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setStepReady: PropTypes.func, // Add this to PropTypes
};
